import {
  Dialog,
  DialogBareContent,
  DialogClose,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@h2hspark/ui';
import { X } from 'lucide-react';
import * as React from 'react';

import { CLOSE_TICKET_LABEL, NO_TICKET_DESCRIPTION, NO_TICKET_TITLE, TICKET_HEADING } from './constants';
import { TicketCard, useTicketContent } from './ticket-card';

/**
 * **The ticket as an overlay** — `Web App.dc.html`'s `ticketOpen` (lines 1137–1148) on the desktop,
 * and `Member App.dc.html`'s full-screen purple ticket on a phone (`MEM-056` AC-3).
 *
 * Desktop: a scrim at `rgba(28,14,48,.6)` and a 380px card at `--radius-xl` with `--shadow-xl`,
 * top-aligned with 24px of padding, as every overlay in that file is. Phone: the card on a
 * `--purple-800` screen with a round close control at the top right, which is what the phone
 * design draws.
 *
 * **The route stays.** `/app/events/:id/ticket` still resolves and renders the same
 * {@link TicketCard} — mailed links point at it (`MEM-047`), and a member who bookmarks their
 * ticket keeps it. This is the same ticket, opened without leaving the page.
 *
 * **The dialog's behaviour is Radix's** rather than hand-rolled: Escape and a click on the scrim
 * close it, focus is trapped inside it while it is open and returns to the control that opened it,
 * and the page behind it is inert. `DialogBareContent` is the content element with none of the
 * console dialog's chrome, so the ticket draws itself.
 */
export function TicketDialog({
  eventId,
  children,
}: {
  readonly eventId: string;
  /** The control that opens it, rendered as the trigger (`asChild`). */
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogPortal>
        <DialogOverlay className="z-[60] bg-[rgba(28,14,48,.6)] backdrop-blur-none" />
        {/* One element at both widths: the phone's purple screen is the same box the desktop
            centres at 380px, so nothing appears at one width and not the other. */}
        <DialogBareContent
          data-testid="ticket-dialog"
          className="fixed inset-0 z-[61] flex flex-col items-center overflow-y-auto bg-brand-800 px-6 pb-7 pt-14 md:inset-x-0 md:bottom-auto md:top-6 md:mx-auto md:w-full md:max-w-[380px] md:overflow-hidden md:rounded-xl md:bg-background md:p-0 md:shadow-xl"
        >
          <DialogTitle className="sr-only">{TICKET_HEADING}</DialogTitle>
          <DialogClose
            aria-label={CLOSE_TICKET_LABEL}
            className="absolute right-5 top-5 inline-flex h-[38px] w-[38px] items-center justify-center rounded-pill bg-white/20 text-white md:bg-black/5 md:text-text-subtle"
          >
            <X className="h-5 w-5" aria-hidden />
          </DialogClose>
          <TicketContents eventId={eventId} />
        </DialogBareContent>
      </DialogPortal>
    </Dialog>
  );
}

function TicketContents({ eventId }: { readonly eventId: string }): React.ReactElement {
  const content = useTicketContent(eventId);

  if (content.state === 'no-ticket') {
    return (
      <div className="w-full max-w-[300px] rounded-xl bg-background p-6 text-center md:max-w-none md:rounded-none md:shadow-none">
        <p className="font-brand text-md font-bold text-foreground">{NO_TICKET_TITLE}</p>
        <p className="mt-1 text-sm text-muted-foreground">{NO_TICKET_DESCRIPTION}</p>
      </div>
    );
  }

  return (
    <>
      <TicketCard
        content={content}
        className="w-full max-w-[300px] overflow-hidden rounded-xl bg-background shadow-xl md:max-w-none md:rounded-none md:shadow-none"
      />
      <DialogClose className="mt-5 w-full max-w-[300px] rounded-pill border-[1.5px] border-border bg-card px-4 py-3 font-brand text-md font-bold text-text-body md:mb-[22px] md:max-w-[336px]">
        {CLOSE_TICKET_LABEL}
      </DialogClose>
    </>
  );
}
